/**
 * The update composer (`specs/M1-B7-item-card-and-updates.md` §2.3).
 *
 * Geometry is the captured one: **668 × 92 at rest, 668 × 192 focused**, a 15-button toolbar
 * on a 32 px pitch, a 666 × 70 editor and a split `Update` button whose primary half is
 * 62 × 32 `[ic§Composer]`. The reply variant drops the toolbar and its primary button reads
 * `Reply` at 51 × 32 `[ic§Reply composer, expanded]`.
 *
 * **Round 5o: the editor is TipTap** (ADR-001), not a `contenteditable` driven by
 * `document.execCommand`. What changes and what deliberately does not:
 *
 *  - the DOM contract is unchanged — same class names, same `data-testid`s, same geometry —
 *    so the 28 hit-tests of `verify-item-card-local.mjs` and the B7 e2e suite still apply;
 *  - the **mention popup is unchanged**: it is still driven by `findMentionTrigger` over the
 *    text before the caret, still renders the same rows, and still lets the server build the
 *    anchor from `mentions_list` `[ua:a06]`. TipTap's own `Mention` node is deliberately not
 *    used for the token, because monday stores the anchor in the HTML body and resolves the
 *    mention server-side;
 *  - `Bold`, `Italic`, `Underline`, `Strikethrough`, `1. Numbers`, `• Bullets`, `Line`,
 *    `Format` (blockquote) and `Link` are now **real** commands rather than `execCommand`;
 *  - `Checklist` becomes real when the build's StarterKit registers a task list — the button
 *    feature-detects `toggleTaskList` rather than assuming it;
 *  - `Change direction` toggles `dir` on the editor, which needs no extension;
 *  - `Text color`, `Size`, `Table` and `Align` **were** unavailable for want of an extension;
 *    **round 6g** added the three packages they need and, first, drove monday's own dropdowns
 *    on the sandbox to find out what they contain — a 264 px panel at the button's x, y 302,
 *    on a 39.5 px row pitch: a 10 × 4 colour grid with `Text` / `Highlight` tabs and `None`,
 *    six font sizes plus `Remove font size`, ten table commands, three alignments
 *    (`discovery/ui-reference/item-card/q253-toolbar-*.json`, `composer-model.ts`). The panels
 *    render from this file (synchronously, like the rest of the chrome); only the commands
 *    they run belong to the lazy TipTap chunk.
 *
 * **Round 5z: TipTap loads on demand.** Every `@tiptap/*` import now lives in
 * `tiptap-runtime.tsx`, reached through `React.lazy`, because it was ~400 kB of an entry
 * bundle that every screen pays for and only this composer uses (`tasks/T-5q-fixes.md` B4,
 * q366). The chrome below — toolbar, insert row, split button, mention popup — still renders
 * **synchronously**, so the captured geometry and every hit test are unchanged; only the
 * editable area arrives a chunk later, behind a placeholder of the same size. The type-only
 * `Editor` import above is erased at compile time and pulls nothing into the entry.
 */
import { Icon, icons } from '@elizmonday/ui';
import { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  ALIGN_MENU_ROWS,
  FONT_SIZES,
  INSERT_BUTTONS,
  TABLE_MENU_ROWS,
  TEXT_COLOR_SWATCHES,
  TOOLBAR_BUTTONS,
  canPost,
  dragHasFiles,
  fileSize,
  filesFromTransfer,
  findMentionTrigger,
  mentionFor,
  mentionOptions,
  opensMenu,
  type MentionOption,
  type MenuButtonId,
} from './composer-model';
import { raiseCardToast } from './CardToast';
import type { CardAsset, CardMention, CardPerson } from './types';

export interface ComposerProps {
  variant: 'update' | 'reply' | 'edit';
  boardId: string;
  itemId: string;
  me: CardPerson | null;
  initialHtml?: string;
  autoFocus?: boolean;
  placeholder?: string;
  submitLabel?: string;
  mentionCandidates(query: string): Promise<CardPerson[]>;
  /**
   * Posts the update. **P-299**: `attachments` are assets that are already uploaded — a paste,
   * a drop or the paperclip put them there — and the caller links each one to whatever update
   * this post creates (`source.attachAsset`), because an upload cannot name an update that did
   * not exist when the image was pasted.
   */
  onSubmit(input: { body: string; mentions: CardMention[]; attachments: CardAsset[] }): Promise<void> | void;
  onCancel?(): void;
  /**
   * Uploads one file **now** and hands back the asset, which the composer shows in its pending
   * strip until the update is posted. That is monday's order, measured: the asset id exists
   * before the update does (`discovery/behavior-notes/P-299-composer-paste.md`). A rejection is
   * the api's own sentence and the composer toasts it verbatim.
   */
  onAttach?(file: File): Promise<CardAsset | null>;
  testId?: string;
}

const AVATAR = (person: CardPerson | null) =>
  (person?.name ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/** The toolbar commands TipTap can express with the four extensions this round installed. */
type ToolAvailability = 'live' | 'unavailable';

/** The editor itself, in its own chunk. Nothing else may import `@tiptap/*` statically. */
const TipTapEditor = lazy(() => import('./tiptap-runtime'));

/**
 * Is this command registered on the editor this build produced?
 *
 * The toolbar is drawn synchronously and the extensions arrive with the lazy chunk, so
 * "which buttons work" is a question about the *instance*, never about the import list.
 */
function hasCommand(editor: Editor, name: string): boolean {
  return typeof (editor.commands as unknown as Record<string, unknown>)[name] === 'function';
}

export function Composer(props: ComposerProps) {
  const {
    variant,
    boardId,
    itemId,
    me,
    initialHtml = '',
    autoFocus = false,
    placeholder = 'Write an update and mention others with @',
    submitLabel = variant === 'reply' ? 'Reply' : 'Update',
    mentionCandidates,
    onSubmit,
    onCancel,
    onAttach,
    testId = 'update-composer',
  } = props;

  const fileRef = useRef<HTMLInputElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  /** The composer's own root: paste and drop are listened for here, in the capture phase. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  // `edit` opens already expanded: the update is *replaced* by the composer in place.
  const [expanded, setExpanded] = useState(variant === 'edit' || autoFocus);
  const [empty, setEmpty] = useState(initialHtml.trim() === '');
  const [busy, setBusy] = useState(false);
  const [rtl, setRtl] = useState(false);
  const [mentions, setMentions] = useState<CardMention[]>([]);
  const [popup, setPopup] = useState<{ options: MentionOption[]; query: string } | null>(null);
  /** Which toolbar dropdown is open (round 6g), and which tab the colour one is showing. */
  const [menu, setMenu] = useState<MenuButtonId | null>(null);
  const [colorTab, setColorTab] = useState<'text' | 'highlight'>('text');
  /**
   * **P-299 — the pending attachments.** Each one is *already uploaded*; what is pending is the
   * link to an update that does not exist yet. `uploading` counts the flights in progress, so
   * the post button cannot fire while an image is still on the wire and lose it.
   */
  const [attachments, setAttachments] = useState<CardAsset[]>([]);
  const [uploading, setUploading] = useState(0);
  /** The drop highlight: monday's composer is wrapped in a dropzone `[P-299 capture]`. */
  const [dropping, setDropping] = useState(false);
  const labelId = useId();

  /** The text of the block the caret is in, up to the caret — what the mention trigger reads. */
  const textBeforeCaret = useCallback((editor: Editor): string => {
    const { from, empty: collapsed } = editor.state.selection;
    if (!collapsed) return '';
    return editor.state.doc.textBetween(Math.max(0, from - 120), from, '\n', '\n');
  }, []);

  const onUpdate = useCallback(
    (editor: Editor) => {
      setEmpty(!canPost(editor.getHTML()));
      const trigger = findMentionTrigger(textBeforeCaret(editor));
      if (!trigger) {
        setPopup(null);
        return;
      }
      void mentionCandidates(trigger.query).then((people) => {
        setPopup({ options: mentionOptions(people, trigger.query), query: trigger.query });
      });
    },
    [mentionCandidates, textBeforeCaret],
  );

  /**
   * The instance the lazy chunk hands back; `null` until it has loaded.
   *
   * It is **another component's** editor now, and `useEditor` destroys and re-creates it on
   * its own schedule (a remount, a changed extension list, its 1 ms "was I really mounted?"
   * timer). A destroyed `Editor` nulls its command manager and *throws* on `.commands`, so a
   * copy that has gone stale here is treated as no editor at all — the toolbar greys out for
   * the frame it takes the runtime to hand back the replacement.
   */
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const editor = editorInstance !== null && !editorInstance.isDestroyed ? editorInstance : null;
  const editorAttributes = {
    class: 'ez-composer__editor',
    role: 'textbox',
    'aria-multiline': 'true',
    'aria-label': 'Text editor',
    'data-testid': `${testId}-editor`,
    'data-placeholder': placeholder,
  };

  useEffect(() => {
    if (!expanded || !editor) return;
    editor.commands.focus('end');
  }, [expanded, editor]);

  /**
   * **Upload what a paste, a drop or the paperclip is carrying** (P-299).
   *
   * One flight per file, in order, through the *same* path the paperclip has always used
   * (`onAttach` → `source.uploadFile` → `POST /files/upload`), because monday uploads a pasted
   * image the moment it is pasted and shows it in the composer while the update is still being
   * written (`discovery/behavior-notes/P-299-composer-paste.md`). A refusal is the api's own
   * sentence — `File exceeds the 500 MB limit`, the view-only refusal — and it goes straight
   * into the card's toast; the files that did upload stay in the strip.
   */
  const addFiles = useCallback(
    async (files: readonly File[]) => {
      if (files.length === 0 || !onAttach) return;
      for (const file of files) {
        setUploading((count) => count + 1);
        try {
          const asset = await onAttach(file);
          if (asset) setAttachments((current) => [...current, asset]);
        } catch (cause) {
          raiseCardToast({ message: (cause as Error).message, kind: 'error' });
        } finally {
          setUploading((count) => count - 1);
        }
      }
    },
    [onAttach],
  );

  /**
   * **Paste and drop, listened for on the composer root in the capture phase.**
   *
   * The capture phase is the point: ProseMirror registers its own `paste` and `drop` handlers
   * on the editable node, and a React `onPaste` on an ancestor would run *after* them — too late
   * to stop the editor making its own mind up about a clipboard that carries an image. Listening
   * on the root with `capture: true` means this runs first, and it consumes the event **only**
   * when the transfer actually carries files: a text paste is never touched, so pasting text
   * still inserts text, exactly as monday's composer does.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !onAttach) return undefined;

    const onPaste = (event: Event) => {
      const files = filesFromTransfer((event as ClipboardEvent).clipboardData);
      if (files.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      void addFiles(files);
    };
    const onDragOver = (event: Event) => {
      if (!dragHasFiles((event as DragEvent).dataTransfer)) return;
      // Without `preventDefault` the browser navigates to the dropped file instead.
      event.preventDefault();
      /*
       * And the editor below must not see it: ProseMirror answers a `dragover` by drawing its
       * **text** drop cursor, which is meaningless for a file — this drag is going to become an
       * attachment, not a caret position.
       */
      event.stopPropagation();
      setDropping(true);
    };
    const onDragLeave = (event: Event) => {
      const next = (event as DragEvent).relatedTarget;
      if (next instanceof Node && root.contains(next)) return;
      setDropping(false);
    };
    const onDrop = (event: Event) => {
      const files = filesFromTransfer((event as DragEvent).dataTransfer);
      setDropping(false);
      if (files.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      void addFiles(files);
    };

    root.addEventListener('paste', onPaste, true);
    root.addEventListener('dragenter', onDragOver, true);
    root.addEventListener('dragover', onDragOver, true);
    root.addEventListener('dragleave', onDragLeave, true);
    root.addEventListener('drop', onDrop, true);
    return () => {
      root.removeEventListener('paste', onPaste, true);
      root.removeEventListener('dragenter', onDragOver, true);
      root.removeEventListener('dragover', onDragOver, true);
      root.removeEventListener('dragleave', onDragLeave, true);
      root.removeEventListener('drop', onDrop, true);
    };
  }, [addFiles, onAttach, expanded]);

  /**
   * Which toolbar buttons actually do something with the extensions we have. `Checklist` is
   * feature-detected because StarterKit's list kit registers `TaskList` in some builds and
   * not others, and a button that silently does nothing is worse than one that says so.
   */
  const availability = useCallback(
    (id: string): ToolAvailability => {
      if (!editor) return 'unavailable';
      switch (id) {
        case 'bold':
        case 'italic':
        case 'underline':
        case 'strikethrough':
        case 'numbers':
        case 'bullets':
        case 'line':
        case 'format':
        case 'link':
        case 'direction':
          return 'live';
        case 'checklist':
          return typeof (editor.commands as Record<string, unknown>).toggleTaskList === 'function'
            ? 'live'
            : 'unavailable';
        // Round 6g: the four menu buttons are real. Each is feature-detected against the
        // command its extension registers, so a build without one greys the button out
        // rather than opening a panel whose rows do nothing.
        case 'color':
          return hasCommand(editor, 'setColor') ? 'live' : 'unavailable';
        case 'size':
          return hasCommand(editor, 'setFontSize') ? 'live' : 'unavailable';
        case 'table':
          return hasCommand(editor, 'insertTable') ? 'live' : 'unavailable';
        case 'align':
          return hasCommand(editor, 'setTextAlign') ? 'live' : 'unavailable';
        default:
          return 'unavailable';
      }
    },
    [editor],
  );

  /**
   * The menus' own commands. They are kept out of `applyFormat` because they take an
   * argument — a colour, a size, a table verb, an alignment — and because closing the menu
   * afterwards is part of the action, not part of the formatting.
   */
  const runMenu = useCallback(
    (id: MenuButtonId, value: string) => {
      setMenu(null);
      if (!editor) return;
      const chain = editor.chain().focus() as unknown as Record<string, (...args: unknown[]) => { run(): void }>;
      switch (id) {
        case 'color':
          // monday's two tabs are two marks: the glyph's colour and the band behind it.
          if (value === 'none') {
            (colorTab === 'text' ? chain.unsetColor() : chain.unsetBackgroundColor()).run();
            return;
          }
          (colorTab === 'text' ? chain.setColor(value) : chain.setBackgroundColor(value)).run();
          return;
        case 'size':
          (value === 'none' ? chain.unsetFontSize() : chain.setFontSize(value)).run();
          return;
        case 'align':
          chain.setTextAlign(value).run();
          return;
        case 'table': {
          const table: Record<string, () => { run(): void }> = {
            'insert-table': () => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
            'row-above': () => chain.addRowBefore(),
            'row-below': () => chain.addRowAfter(),
            'column-left': () => chain.addColumnBefore(),
            'column-right': () => chain.addColumnAfter(),
            // monday says "head"; ProseMirror's table calls the same thing a header row.
            'add-head': () => chain.toggleHeaderRow(),
            'delete-head': () => chain.toggleHeaderRow(),
            'delete-column': () => chain.deleteColumn(),
            'delete-row': () => chain.deleteRow(),
            'delete-table': () => chain.deleteTable(),
          };
          table[value]?.().run();
          return;
        }
      }
    },
    [colorTab, editor],
  );

  const applyFormat = useCallback(
    (id: string) => {
      if (!editor) return;
      if (opensMenu(id)) {
        setMenu((current) => (current === id ? null : id));
        return;
      }
      const chain = editor.chain().focus();
      switch (id) {
        case 'bold':
          chain.toggleBold().run();
          return;
        case 'italic':
          chain.toggleItalic().run();
          return;
        case 'underline':
          chain.toggleUnderline().run();
          return;
        case 'strikethrough':
          chain.toggleStrike().run();
          return;
        case 'numbers':
          chain.toggleOrderedList().run();
          return;
        case 'bullets':
          chain.toggleBulletList().run();
          return;
        case 'line':
          chain.setHorizontalRule().run();
          return;
        case 'format':
          chain.toggleBlockquote().run();
          return;
        case 'checklist': {
          const commands = editor.commands as unknown as Record<string, () => boolean>;
          if (typeof commands.toggleTaskList === 'function') commands.toggleTaskList();
          return;
        }
        case 'direction':
          // No extension needed: the direction is a DOM attribute on the editor itself.
          setRtl((current) => !current);
          return;
        case 'link': {
          const href = window.prompt('Link URL');
          if (!href) return;
          chain.extendMarkRange('link').setLink({ href }).run();
          return;
        }
        default:
          return;
      }
    },
    [editor],
  );

  const choose = useCallback(
    (option: MentionOption) => {
      if (editor) {
        const before = textBeforeCaret(editor);
        const trigger = findMentionTrigger(before);
        if (trigger) {
          // Replace the `@query` token with the label; the anchor itself is appended by the
          // server, exactly as monday's `mentions_list` does `[ua:a06]`.
          const { from } = editor.state.selection;
          const start = from - (before.length - trigger.start);
          editor.chain().focus().insertContentAt({ from: start, to: from }, `@${option.label} `).run();
        }
      }
      setMentions((current) => [...current, mentionFor(option, { boardId, itemId })]);
      setPopup(null);
      if (editor) setEmpty(!canPost(editor.getHTML()));
    },
    [boardId, editor, itemId, textBeforeCaret],
  );

  const submit = useCallback(async () => {
    if (!editor || busy || uploading > 0) return;
    const body = editor.getHTML();
    // P-299: an image on its own is a post, exactly as it is in monday — the body is empty and
    // the update is the picture. `canPost` alone would refuse it.
    if (!canPost(body) && attachments.length === 0) return;
    setBusy(true);
    try {
      await onSubmit({ body, mentions, attachments });
      editor.commands.clearContent();
      setMentions([]);
      setAttachments([]);
      setEmpty(true);
      if (variant === 'update') setExpanded(false);
    } finally {
      setBusy(false);
    }
  }, [attachments, busy, editor, mentions, onSubmit, uploading, variant]);

  /**
   * One of monday's four toolbar dropdowns, drawn to the capture: 264 px wide, opening at the
   * button, rows on a 39.5 px pitch `[ic§q253-toolbar-*]`.
   */
  function renderMenu(id: MenuButtonId) {
    const rows =
      id === 'size'
        ? [...FONT_SIZES.map((size) => ({ id: size, label: size })), { id: 'none', label: 'Remove font size' }]
        : id === 'table'
          ? [...TABLE_MENU_ROWS]
          : id === 'align'
            ? [...ALIGN_MENU_ROWS]
            : [];
    return (
      <div
        className={`ez-composer__menu ez-composer__menu--${id}`}
        role="menu"
        aria-label={TOOLBAR_BUTTONS.find((button) => button.id === id)?.label}
        data-testid={`composer-menu-${id}`}
        onMouseDown={(event) => event.preventDefault()}
      >
        {id === 'color' ? (
          <>
            <div className="ez-composer__menu-tabs" role="tablist">
              {(['text', 'highlight'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={colorTab === tab}
                  className="ez-composer__menu-tab"
                  data-testid={`composer-color-tab-${tab}`}
                  onClick={() => setColorTab(tab)}
                >
                  {tab === 'text' ? 'Text' : 'Highlight'}
                </button>
              ))}
            </div>
            <div className="ez-composer__menu-grid">
              {TEXT_COLOR_SWATCHES.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  role="menuitem"
                  className="ez-composer__menu-swatch"
                  style={{ background: swatch }}
                  aria-label={swatch}
                  data-testid={`composer-color-${swatch.replace(/[^0-9]+/g, '-')}`}
                  onClick={() => runMenu('color', swatch)}
                />
              ))}
            </div>
            <button
              type="button"
              role="menuitem"
              className="ez-composer__menu-row"
              data-testid="composer-color-none"
              onClick={() => runMenu('color', 'none')}
            >
              None
            </button>
          </>
        ) : (
          rows.map((row) => (
            <button
              key={row.id}
              type="button"
              role="menuitem"
              className="ez-composer__menu-row"
              data-testid={`composer-menu-${id}-${row.id}`}
              onClick={() => runMenu(id, row.id)}
            >
              {row.label}
            </button>
          ))
        )}
      </div>
    );
  }

  if (!expanded) {
    return (
      <div
        className={`ez-composer ez-composer--collapsed ez-composer--${variant}`}
        data-testid={`${testId}-collapsed`}
        role="button"
        tabIndex={0}
        aria-label={placeholder}
        onClick={() => setExpanded(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded(true);
          }
        }}
      >
        <div className="ez-composer__placeholder" data-testid={`${testId}-placeholder`}>
          {placeholder}
        </div>
        <div className="ez-composer__insert-row" aria-hidden="true">
          {INSERT_BUTTONS.map((button) => (
            <span key={button.id} className="ez-composer__insert" data-new={button.isNew || undefined}>
              {button.id === 'format' ? <Icon icon={icons.textFormatting} size={16} ignoreFocusStyle /> : button.glyph}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`ez-composer ez-composer--expanded ez-composer--${variant}${dropping ? ' ez-composer--dropping' : ''}`}
      data-testid={testId}
      data-variant={variant}
      data-dropping={dropping || undefined}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          /*
           * **F4 P5 — Escape closes exactly one layer at a time, topmost first**, and the
           * composer has three of them: the mention popup, a toolbar dropdown, and the
           * expanded editor itself. `stopPropagation` is what keeps each of them *one* layer:
           * `ItemCard` listens for Escape on `window` to close the whole panel, so without it
           * closing a colour picker closed the item card.
           *
           * **P-169**: the popup was the layer that was missing. Dismissing the `@` popup
           * cleared it and then let the key through, so the card closed and the typed update
           * went with it — there is no draft recovery, so the keystroke was unrecoverable.
           */
          if (popup !== null) {
            event.stopPropagation();
            setPopup(null);
            return;
          }
          if (menu !== null) {
            event.stopPropagation();
            setMenu(null);
            return;
          }
          setPopup(null);
          /*
           * And the editor itself is a layer. `onCancel` belongs to the variants that have a
           * cancel — an edit in place and a reply, where Escape abandons *that* editor and
           * nothing else. The update composer has none, so the key used to reach the card's
           * `window` listener and close the panel over a half-written update.
           *
           * **ASSUMED (q544)**: monday keeps an update draft and this product keeps none, so
           * the conservative reading is the one that cannot lose text — Escape leaves the
           * editor (it blurs) and the body stays exactly where it is. An **empty** expanded
           * composer has nothing to lose, so it collapses back to its resting state, which is
           * the same "leave the editor" gesture with nothing left behind.
           */
          if (onCancel) {
            onCancel();
            return;
          }
          event.stopPropagation();
          editor?.commands.blur();
          if (empty) setExpanded(false);
        }
        // monday posts on Ctrl/Cmd+Enter; a bare Enter is a newline in a rich body.
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          void submit();
        }
      }}
    >
      {variant === 'reply' ? null : (
        <div
          ref={toolbarRef}
          className="ez-composer__toolbar"
          role="toolbar"
          aria-labelledby={labelId}
          data-testid={`${testId}-toolbar`}
        >
          <span id={labelId} className="ez-visually-hidden">
            Formatting
          </span>
          {TOOLBAR_BUTTONS.map((button) => {
            const state = availability(button.id);
            const active = editor?.isActive(button.id === 'format' ? 'blockquote' : button.id) ?? false;
            const hasMenu = opensMenu(button.id);
            const control = (
              <button
                key={button.id}
                type="button"
                className="ez-composer__tool"
                aria-label={button.label}
                title={state === 'live' ? button.label : `${button.label} (not available yet)`}
                aria-pressed={state === 'live' && !hasMenu ? active : undefined}
                aria-expanded={hasMenu ? menu === button.id : undefined}
                aria-haspopup={hasMenu ? 'menu' : undefined}
                aria-disabled={state === 'unavailable' || undefined}
                data-state={state}
                data-testid={`composer-tool-${button.id}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applyFormat(button.id)}
              >
                {button.glyph}
              </button>
            );
            if (!hasMenu) return control;
            /*
             * The panel is anchored to its own button: monday opens each one at the button's
             * x and 32 px below the toolbar row (measured 919/951/1047/1111 → y 302 for a
             * toolbar at y 270 `[ic§q253-toolbar-*]`).
             */
            return (
              <span key={button.id} className="ez-composer__tool-anchor">
                {control}
                {menu === button.id ? renderMenu(button.id) : null}
              </span>
            );
          })}
        </div>
      )}

      {/*
        * The placeholder keeps the 666 × 70 band while the chunk arrives, and deliberately
        * carries **no** `data-testid`: a click target that is not the editor would swallow
        * the first keystroke, so the suites wait for the real element instead.
        */}
      <Suspense fallback={<div className="ez-composer__editor ez-composer__editor--loading" aria-hidden="true" />}>
        <TipTapEditor
          placeholder={placeholder}
          initialHtml={initialHtml}
          autofocus={expanded}
          attributes={editorAttributes}
          dir={rtl ? 'rtl' : undefined}
          onUpdate={onUpdate}
          onEditor={setEditorInstance}
        />
      </Suspense>

      {popup ? (
        <div className="ez-mention-popup" role="listbox" data-testid="mention-popup">
          {popup.options.map((option, index) => (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={index === 0}
              className="ez-mention-popup__row"
              data-testid={`mention-option-${option.key}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      {/*
        * **P-299 — the pending attachments.** monday puts the pasted image *inline in the body*
        * as a `redactor-uploaded-figure` and posts it as an `Update.asset`
        * (`discovery/behavior-notes/P-299-composer-paste.md`); our body is TipTap and nothing on
        * the server links an `<img>` in a body to `update_assets`, so the same information is
        * carried by a strip under the editor and attached to the update when it is posted. The
        * remove control is the gesture monday has as ordinary editing — select the figure,
        * Backspace — and a removed file simply stays an uploaded item asset, which is exactly
        * what monday leaves behind for an image deleted before posting.
        */}
      {attachments.length > 0 || uploading > 0 ? (
        <ul className="ez-composer__attachments" data-testid={`${testId}-attachments`}>
          {attachments.map((asset) => (
            <li key={asset.id} className="ez-composer__attachment" data-testid={`${testId}-attachment-${asset.id}`}>
              {/^image\//i.test(asset.mime) && !/^image\/svg/i.test(asset.mime) ? (
                <img
                  className="ez-composer__attachment-thumb"
                  src={asset.thumbnailUrl ?? asset.url}
                  alt={asset.name}
                  data-testid={`${testId}-attachment-${asset.id}-thumb`}
                />
              ) : (
                <span className="ez-composer__attachment-glyph" aria-hidden="true">
                  {/* The paperclip the insert row already uses for a file `[ic§Composer]`. */}
                  📎
                </span>
              )}
              <span className="ez-composer__attachment-name" title={asset.name}>
                {asset.name}
              </span>
              <span className="ez-composer__attachment-size">{fileSize(asset.size)}</span>
              <button
                type="button"
                className="ez-composer__attachment-remove"
                aria-label={`Remove ${asset.name}`}
                title={`Remove ${asset.name}`}
                data-testid={`${testId}-attachment-${asset.id}-remove`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setAttachments((current) => current.filter((entry) => entry.id !== asset.id))}
              >
                ×
              </button>
            </li>
          ))}
          {uploading > 0 ? (
            <li className="ez-composer__attachment ez-composer__attachment--uploading" data-testid={`${testId}-attachment-uploading`}>
              Uploading…
            </li>
          ) : null}
        </ul>
      ) : null}

      <div className="ez-composer__footer">
        <div className="ez-composer__insert-row">
          {INSERT_BUTTONS.map((button) => (
            <button
              key={button.id}
              type="button"
              className="ez-composer__insert"
              aria-label={button.label}
              title={button.label}
              data-new={button.isNew || undefined}
              data-testid={`composer-insert-${button.id}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (button.id === 'file') fileRef.current?.click();
                if (button.id === 'mention' && editor) {
                  editor.chain().focus().insertContent('@').run();
                  onUpdate(editor);
                }
                /* The toolbar is already open in this state; `Formatting` moves the caret to
                   it so the row is reachable from the keyboard as well as the mouse. */
                if (button.id === 'format') {
                  const first = toolbarRef.current?.querySelector('button');
                  (first as HTMLButtonElement | null)?.focus();
                }
              }}
            >
              {button.id === 'format' ? <Icon icon={icons.textFormatting} size={16} ignoreFocusStyle /> : button.glyph}
            </button>
          ))}
          {/* monday's own input carries `multiple` and **no** `accept` — it refuses nothing by
              type here `[P-299 capture]`. The picked files take the same road as a paste. */}
          <input
            ref={fileRef}
            type="file"
            multiple
            className="ez-visually-hidden"
            data-testid={`${testId}-file-input`}
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              event.target.value = '';
              void addFiles(picked);
            }}
          />
        </div>

        <div className="ez-composer__submit">
          {onCancel ? (
            <button type="button" className="ez-composer__cancel" data-testid={`${testId}-cancel`} onClick={onCancel}>
              Cancel
            </button>
          ) : null}
          {/* monday's split button: a 62 × 32 primary and a 29 × 32 secondary `[ic§Composer]`. */}
          <button
            type="button"
            className="ez-composer__post"
            data-testid={`${testId}-submit`}
            disabled={(empty && attachments.length === 0) || busy || uploading > 0}
            onClick={() => void submit()}
          >
            {submitLabel}
          </button>
          <button
            type="button"
            className="ez-composer__post-more"
            aria-label="additional actions"
            data-testid={`${testId}-submit-more`}
            disabled={(empty && attachments.length === 0) || busy || uploading > 0}
          >
            ▾
          </button>
        </div>
      </div>

      {me ? (
        <span className="ez-visually-hidden" data-testid={`${testId}-author`}>
          {AVATAR(me)}
        </span>
      ) : null}
    </div>
  );
}
